import { lowerDecodeLimit, type ProjectDecodeLimits } from './ProjectDecodeLimits';
import type { MetadataScalar } from './MetadataScalars';

/** Disk-only paths: [previous prefix length, numeric kind, ...atom references]. */
export interface PackedMetadataScalars {
  atoms: string[];
  rows: number[][];
}
const MAX_DEPTH = 128;
const MAX_PARTS = 2_000_000;
// UTF-16 units, charged independently for the dictionary and every expanded
// occurrence (including reused prefixes). No giant expanded JSON is allocated.
const MAX_TEXT = 64 * 1024 * 1024;

class PathBudget {
  private maxParts: number;
  private maxText: number;
  constructor(limits?: ProjectDecodeLimits) {
    this.maxParts = lowerDecodeLimit(limits?.scalarParts, MAX_PARTS);
    this.maxText = lowerDecodeLimit(limits?.scalarText, MAX_TEXT);
  }
  parts = 0;
  text = 0;
  charge(parts: number, text: number) {
    if (
      parts < 1 ||
      parts > MAX_DEPTH ||
      (this.parts += parts) > this.maxParts ||
      (this.text += text) > this.maxText
    )
      throw new RangeError('Packed metadata scalar expansion exceeds limit');
  }
}

export function packMetadataScalars(
  input: MetadataScalar[] | undefined,
): PackedMetadataScalars | undefined {
  if (input === undefined) return undefined;
  const atoms: string[] = [],
    rows: number[][] = [];
  const indexes = new Map<string, number>();
  const budget = new PathBudget();
  let dictionaryText = 0;
  let previous: string[] = [];
  for (const [path, kind] of input) {
    if (
      !Array.isArray(path) ||
      !path.every((key) => typeof key === 'string') ||
      (kind !== 'undefined' && kind !== '-0')
    )
      throw new TypeError('Invalid metadata scalar path');
    budget.charge(
      path.length,
      path.reduce((sum, key) => sum + key.length, 0),
    );
    let prefix = 0;
    while (
      prefix < previous.length &&
      prefix < path.length &&
      previous[prefix] === path[prefix]
    )
      prefix++;
    const row = [prefix, kind === 'undefined' ? 0 : 1];
    for (let i = prefix; i < path.length; i++) {
      const key = path[i];
      let index = indexes.get(key);
      if (index === undefined) {
        if (atoms.length >= MAX_PARTS || (dictionaryText += key.length) > MAX_TEXT)
          throw new RangeError('Packed metadata scalar dictionary exceeds limit');
        index = atoms.length;
        indexes.set(key, index);
        atoms.push(key);
      }
      row.push(index);
    }
    rows.push(row);
    previous = path;
  }
  return { atoms, rows };
}

/** Validate and charge each expanded path before allocating it. Logical scalar
 * restoration still applies own-property, overwrite and schema checks later. */
export function unpackMetadataScalars(
  input: unknown,
  visit?: (entry: MetadataScalar) => void,
  limits?: ProjectDecodeLimits,
): MetadataScalar[] | undefined {
  if (input === undefined) return undefined;
  if (
    !input ||
    typeof input !== 'object' ||
    Array.isArray(input) ||
    Object.keys(input).length !== 2 ||
    !Object.hasOwn(input, 'atoms') ||
    !Object.hasOwn(input, 'rows')
  )
    throw new TypeError('Invalid packed metadata scalars');
  const { atoms, rows } = input as PackedMetadataScalars;
  if (
    !Array.isArray(atoms) ||
    atoms.length > MAX_PARTS ||
    !Array.isArray(rows) ||
    rows.length > MAX_PARTS
  )
    throw new TypeError('Invalid packed metadata scalar tables');
  let dictionaryText = 0;
  for (const atom of atoms) {
    if (typeof atom !== 'string') throw new TypeError('Invalid metadata scalar atom');
    if ((dictionaryText += atom.length) > MAX_TEXT)
      throw new RangeError('Packed metadata scalar dictionary exceeds limit');
  }
  const output: MetadataScalar[] = [];
  const budget = new PathBudget(limits);
  let previous: string[] = [];
  for (const row of rows) {
    if (
      !Array.isArray(row) ||
      row.length < 2 ||
      row.length > MAX_DEPTH + 2 ||
      !Number.isSafeInteger(row[0]) ||
      row[0] < 0 ||
      row[0] > previous.length ||
      (row[1] !== 0 && row[1] !== 1)
    )
      throw new TypeError('Invalid packed metadata scalar row');
    const prefix = row[0];
    let text = 0;
    for (let i = 0; i < prefix; i++) text += previous[i].length;
    for (let i = 2; i < row.length; i++) {
      const ref = row[i];
      if (!Number.isSafeInteger(ref) || ref < 0 || ref >= atoms.length)
        throw new TypeError('Invalid packed metadata scalar reference');
      text += atoms[ref].length;
    }
    budget.charge(prefix + row.length - 2, text);
    const path = previous.slice(0, prefix);
    for (let i = 2; i < row.length; i++) {
      const atom = atoms[row[i]];
      path.push(atom);
    }
    const entry: MetadataScalar = [path, row[1] === 0 ? 'undefined' : '-0'];
    visit?.(entry);
    output.push(entry);
    previous = path;
  }
  return output;
}
